/**
 * User Consent Service
 * Handles checking and saving user privacy consent for legacy users
 * GDPR Art. 7 - Consent verification and persistence
 */

import { supabase } from '@/lib/supabase/client';
import { getClientIP } from '@/utils/clientIP';
import { debugLog, debugError } from '@/utils/debugLogger';

/** Current privacy policy version - update this to force re-acceptance */
export const PRIVACY_POLICY_VERSION = '1.0';

/**
 * Check if a user has accepted the privacy policy
 * Returns true if privacy_accepted is explicitly true
 */
export async function checkUserConsent(userId: string): Promise<boolean> {
  try {
    debugLog('auth', `Checking consent for user: ${userId}`, undefined, 'UserConsentService');
    
    const { data, error } = await supabase
      .from('user_preferences')
      .select('privacy_accepted, privacy_policy_version')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (error) {
      debugError('auth', 'Error checking user consent:', error, 'UserConsentService');
      // In case of error, assume consent is needed to be safe
      return false;
    }
    
    // If no record exists, privacy_accepted is false, or version doesn't match, consent is required
    const hasConsent = data?.privacy_accepted === true && data?.privacy_policy_version === PRIVACY_POLICY_VERSION;
    
    debugLog('auth', `User consent status: ${hasConsent ? 'granted' : 'required'}`, undefined, 'UserConsentService');
    
    return hasConsent;
  } catch (error) {
    debugError('auth', 'Exception checking user consent:', error, 'UserConsentService');
    return false;
  }
}

/**
 * Save user consent after legacy user accepts privacy policy
 * Updates user_preferences and logs to consent_logs for GDPR audit trail
 */
export async function saveUserConsent(
  userId: string, 
  marketingConsent: boolean
): Promise<boolean> {
  try {
    debugLog('auth', `Saving consent for user: ${userId}, marketing: ${marketingConsent}`, undefined, 'UserConsentService');
    
    const ipAddress = await getClientIP();
    const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : null;
    const now = new Date().toISOString();
    
    // 1. Upsert user_preferences with consent data
    const { error: prefsError } = await supabase
      .from('user_preferences')
      .upsert({
        user_id: userId,
        privacy_accepted: true,
        privacy_accepted_at: now,
        privacy_policy_version: PRIVACY_POLICY_VERSION,
        marketing_consent: marketingConsent,
        marketing_consent_at: marketingConsent ? now : null,
      }, { onConflict: 'user_id' });
    
    if (prefsError) {
      debugError('auth', 'Failed to upsert user_preferences:', prefsError, 'UserConsentService');
      // Continue anyway - consent_logs are more important for GDPR
    } else {
      debugLog('auth', 'user_preferences upserted successfully', undefined, 'UserConsentService');
    }
    
    // 2. Log PRIVACY_POLICY consent (Art. 7 GDPR)
    const { error: privacyLogError } = await supabase
      .from('consent_logs')
      .insert({
        user_id: userId,
        consent_type: 'PRIVACY_POLICY',
        granted: true,
        source: 'legacy_user_consent_dialog',
        ip_address: ipAddress,
        user_agent: userAgent,
      });
    
    if (privacyLogError) {
      debugError('auth', 'Failed to log PRIVACY_POLICY consent:', privacyLogError, 'UserConsentService');
    } else {
      debugLog('auth', 'PRIVACY_POLICY consent logged', undefined, 'UserConsentService');
    }
    
    // 3. Log MARKETING consent (Art. 7 GDPR - accountability requires logging even if false)
    const { error: marketingLogError } = await supabase
      .from('consent_logs')
      .insert({
        user_id: userId,
        consent_type: 'MARKETING',
        granted: marketingConsent,
        source: 'legacy_user_consent_dialog',
        ip_address: ipAddress,
        user_agent: userAgent,
      });
    
    if (marketingLogError) {
      debugError('auth', 'Failed to log MARKETING consent:', marketingLogError, 'UserConsentService');
    } else {
      debugLog('auth', `MARKETING consent logged: ${marketingConsent}`, undefined, 'UserConsentService');
    }
    
    // Consider success if at least privacy consent was logged
    const success = !privacyLogError;
    
    if (success) {
      debugLog('auth', 'User consent saved successfully', undefined, 'UserConsentService');
    }
    
    return success;
  } catch (error) {
    debugError('auth', 'Exception saving user consent:', error, 'UserConsentService');
    return false;
  }
}
